import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useSession } from '../state/useSession';
import { useBuildRun } from '../state/buildRun';
import { addCard, creditBalance, openBillingPortal, type CreditBalance } from '../api/billing';
import { longDate, planLabel } from '../state/plans';
import { PRIVACY_URL, TERMS_URL } from '@/config/legal';
import ReportProblem from '../shell/ReportProblem';
import type { Tier } from '@/policy/limits';
import { deletionAlertTitle, type DeletionOutcome } from '@/account/deleteAccount';
import {
  DELETE_ACCOUNT_TITLE,
  DELETE_ACCOUNT_WARNING,
  planSummary,
} from '@/components/account/accountView';
import { Button, ListRow, Screen, SectionLabel } from '../ui';
import RedeemCode, { type Redeemed } from './account/RedeemCode';
import ChangePassword from './account/ChangePassword';
import { deleteAccountEverywhere } from './account/deleteAccount';

const TIERS: readonly Tier[] = ['free', 'paid', 'unlimited'];

/**
 * Ported from src/app/(main)/account.tsx, section for section and in the same words: Security,
 * Help, Subscription, Credits, Admin, Danger zone.
 *
 * Two of the phone's sections are not here. AI data sharing is the phone's consent record, kept
 * on the device; the web has no such gate yet. Blocked creators belongs to Discover.
 *
 * Credits is where the web goes FURTHER than the phone: the iOS app no longer shows Redeem code
 * (App Store 3.1.1), so this is where codes are redeemed.
 */
export default function Account() {
  const navigate = useNavigate();
  const { user, signOut } = useSession();
  const dialog = useRef<HTMLDialogElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);
  const [portalNotice, setPortalNotice] = useState<string | null>(null);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [deletion, setDeletion] = useState<Exclude<DeletionOutcome, { status: 'deleted' }> | null>(null);
  // Unknown until the server answers; unknown shows nothing rather than a wrong balance.
  const [tier, setTier] = useState<Tier | null>(null);
  const [credits, setCredits] = useState<number | null>(null);
  /** The balance as /credits said it, for the plan rows; null until it answers, or if it fails. */
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  /*
   * A build outlives the page it began on (Keep browsing), and its publish reads the token when it
   * lands: signed out, or back in as somebody else, it went out as them (review of the Account
   * tab, 2026-09-30). The build store now stops rather than publish into another account; this
   * keeps the door shut while it runs, so nobody loses a build they are paying for by accident.
   */
  const building = useBuildRun((s) => s.status === 'running');

  /*
   * `?cancel=1` (2026-10-27): the trial emails' "Cancel or change plan" lands here — signed in first
   * by the route's guard, which keeps the address — and an account with a plan goes straight on to
   * Stripe's portal, as the row would. Without a plan, or with a balance that could not be read, the
   * screen shows as usual, the row there to press. Taken off the address at once, so a reload or
   * the browser's Back does not open the portal again.
   */
  const [params, setParams] = useSearchParams();
  const cancelAsked = useRef(params.get('cancel') === '1');
  /*
   * And `?card=1` (2026-11-01): a no-card trial's emails' "Add a card" goes straight on to Stripe's card
   * page while the account still needs one; `?card=added` is the way back from it.
   */
  const cardAsked = useRef(params.get('card') === '1');
  const [cardAdded] = useState(() => params.get('card') === 'added');
  useEffect(() => {
    if (!params.has('cancel') && !params.has('card')) return;
    const next = new URLSearchParams(params);
    next.delete('cancel');
    next.delete('card');
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let live = true;
    void creditBalance()
      .then((b) => {
        if (!live) return;
        setCredits(b.creditsRemaining);
        setTier(TIERS.includes(b.tier as Tier) ? (b.tier as Tier) : 'free');
        setBalance(b);
        if (cancelAsked.current && (b.plan !== undefined || b.subscribed === true)) {
          cancelAsked.current = false;
          void manageSubscription();
        } else if (cardAsked.current && b.needsCard) {
          cardAsked.current = false;
          void saveCard();
        }
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  function redeemed(result: Redeemed): void {
    if (typeof result.creditsRemaining === 'number') setCredits(result.creditsRemaining);
    if (result.tier && TIERS.includes(result.tier as Tier)) setTier(result.tier as Tier);
  }

  /**
   * The phone's deletion, outcome for outcome (`runAccountDeletion`): data first, then the login,
   * and nothing rounded up to success. A clean deletion signs out and leaves. Anything else is
   * said, under the phone's title for it — and when the account IS closed, signing out waits for
   * the person to have read why their data is not all gone.
   */
  async function reallyDelete(): Promise<void> {
    setBusy(true);
    setError(null);
    // A retry after "Nothing was deleted" must not leave that sentence up over the new attempt.
    setDeletion(null);
    try {
      const outcome = await deleteAccountEverywhere();
      dialog.current?.close();
      if (outcome.status === 'deleted') {
        await leave();
        return;
      }
      setDeletion(outcome);
    } finally {
      setBusy(false);
    }
  }

  async function leave(): Promise<void> {
    await signOut().catch(() => undefined);
    navigate('/', { replace: true });
  }

  /**
   * Out through the same door they came in.
   *
   * Someone who subscribed before their Stripe customer id was stored is found by an email
   * lookup on the first visit, so the first press can take a few seconds — which is why
   * the row shows it is working rather than looking ignored.
   */
  /** "Add a card": Stripe's page that saves one for the trial's first charge, and back here. */
  async function saveCard(): Promise<void> {
    setPortalBusy(true);
    setPortalNotice(null);
    setError(null);
    const result = await addCard();
    if (result.status === 'signin_required') setError('Sign in again to add a card.');
    else if (result.status === 'error') setError(result.message);
    if (result.status !== 'redirecting') setPortalBusy(false);
  }

  async function manageSubscription(): Promise<void> {
    setPortalBusy(true);
    setPortalNotice(null);
    setError(null);
    try {
      const result = await openBillingPortal();
      if (result.status === 'managed_elsewhere') {
        // Not a failure: they pay through the App Store, so this is simply the wrong door.
        setPortalNotice('You subscribed through the App Store, so your subscription is managed there — on your iPhone, in Settings › Apple Account › Subscriptions.');
      } else if (result.status === 'signin_required') {
        setError('Sign in again to manage your subscription.');
      } else if (result.status === 'error') {
        setError(result.message);
      }
      // 'redirecting' needs nothing: the browser is already on its way to Stripe.
    } catch {
      /*
       * `openBillingPortal` answers with a result rather than throwing, so this should be
       * unreachable — which is exactly why it is here. A row that resets to "Cancel or
       * change plan" having silently done nothing is indistinguishable from one that was
       * never pressed.
       */
      setError('Could not open the billing portal. Try again in a moment.');
    } finally {
      setPortalBusy(false);
    }
  }

  const accountClosed = deletion?.status === 'deleted_with_remainder';

  /*
   * The plan rows (2026-10-27). An account KNOWN to have no plan is offered the plans, on Credits:
   * "Manage subscription" sent it to the billing portal, which found no Stripe customer and told it
   * to look in the App Store — wrong for everybody who never subscribed. Anything else, including a
   * balance that has not answered, keeps the way out to the portal: somebody paying must always be
   * able to cancel, so the unknown case is the one that can.
   */
  const hasPlan = balance !== null && (balance.plan !== undefined || balance.subscribed === true);
  const knownNoPlan = balance !== null && !hasPlan && balance.tier !== 'unlimited';
  const planName = balance === null || balance.tier === 'unlimited'
    ? null
    : balance.plan ? planLabel(balance.plan) : balance.subscribed === true ? 'Subscribed' : 'No plan';
  const planDate = balance?.cancelAt && longDate(balance.cancelAt)
    ? `Cancels on ${longDate(balance.cancelAt)}`
    : balance?.trialEndsAt && longDate(balance.trialEndsAt)
      ? balance.needsCard
        ? `Trial ends ${longDate(balance.trialEndsAt)}. Add a card to keep your site live after that.`
        : `Trial ends ${longDate(balance.trialEndsAt)}`
      : null;

  return (
    <Screen title="Account">
      {user?.email && <p className="type-body project-meta">{user.email}</p>}
      {tier !== null && credits !== null && (
        <p className="type-caption plan-detail">{planSummary(tier, credits)}</p>
      )}

      <SectionLabel>Security</SectionLabel>
      <div className="card clay-card detail-rows">
        <ListRow icon="password" label="Change password" onPress={() => setPasswordOpen(true)} divider />
        <ListRow
          icon="signOut"
          label="Sign out"
          accent
          disabled={building}
          onPress={() => void signOut().then(() => navigate('/'))}
        />
      </div>
      {building && (
        <p role="status" className="type-caption plan-detail">
          A build is running. Sign out, or delete the account, once it has finished — until then it is being made for this account.
        </p>
      )}

      {/*
        * Help. "Contact support" was a mailto to support@saddle-sites.com, and the domain has no
        * MX record: every message bounced (found 2026-10-01). Report a problem goes to the server,
        * which keeps it and emails the owner, so it arrives whatever the inbox situation.
        */}
      <SectionLabel>Help</SectionLabel>
      <p className="type-body"><ReportProblem /></p>
      <div className="card clay-card detail-rows">
        <ListRow icon="account" label="Terms of use" onPress={() => window.open(TERMS_URL, '_blank', 'noreferrer')} divider />
        <ListRow icon="account" label="Privacy policy" onPress={() => window.open(PRIVACY_URL, '_blank', 'noreferrer')} />
      </div>

      <SectionLabel>Subscription</SectionLabel>
      {planName && (
        <p className="type-body account-plan">
          <span className="account-plan-name">{planName}</span>
          {planDate && <span className="type-caption plan-detail"> · {planDate}</span>}
        </p>
      )}
      <div className="card clay-card detail-rows">
        {balance?.needsCard && !cardAdded && (
          <ListRow icon="account" label={portalBusy ? 'Opening…' : 'Add a card'} onPress={() => void saveCard()} divider />
        )}
        {knownNoPlan ? (
          <ListRow icon="account" label="Choose a plan" onPress={() => navigate('/credits')} divider />
        ) : (
          <ListRow
            icon="account"
            label={portalBusy ? 'Opening…' : 'Cancel or change plan'}
            onPress={() => void manageSubscription()}
            divider
          />
        )}
        {/* Web only: the browser buys credits through Stripe Checkout on that screen. */}
        <ListRow icon="password" label="Credits" onPress={() => navigate('/credits')} />
      </div>
      {portalNotice && <p role="status" className="type-caption plan-detail">{portalNotice}</p>}
      {cardAdded && <p role="status" className="type-caption plan-detail">Card added. Your plan carries on after the trial, and we’ll remind you before it’s charged.</p>}

      <SectionLabel>Credits</SectionLabel>
      <RedeemCode tier={tier} onRedeemed={redeemed} />

      {tier === 'unlimited' && (
        <>
          <SectionLabel>Admin</SectionLabel>
          <div className="card clay-card detail-rows">
            <ListRow icon="llmSettings" label="Analytics" onPress={() => navigate('/admin/metrics')} />
          </div>
        </>
      )}

      <SectionLabel>Danger zone</SectionLabel>
      <div className="card clay-card detail-rows">
        <ListRow icon="delete" label="Delete account" destructive disabled={building} onPress={() => dialog.current?.showModal()} />
      </div>

      {error && <p role="alert" className="type-caption field-error-text">{error}</p>}

      {deletion && (
        <div role="alert" className="card clay-card">
          <h2 className="type-title">{deletionAlertTitle(deletion.status)}</h2>
          <p className="type-body">{deletion.message}</p>
          {accountClosed && <Button title="OK" onPress={() => void leave()} />}
        </div>
      )}

      {/* A <dialog>, not window.confirm: a native modal blocks the page and cannot be tested. */}
      <dialog ref={dialog} aria-label="Delete your account" className="confirm-dialog">
        <h2 className="type-title">{DELETE_ACCOUNT_TITLE}</h2>
        {DELETE_ACCOUNT_WARNING.split('\n\n').map((paragraph) => (
          <p key={paragraph} className="type-body">{paragraph}</p>
        ))}
        <div className="build-actions">
          <Button title="Delete" variant="dangerSoft" loading={busy} onPress={() => void reallyDelete()} />
          <Button title="Cancel" variant="ghost" onPress={() => dialog.current?.close()} />
        </div>
      </dialog>

      <ChangePassword open={passwordOpen} email={user?.email ?? ''} onClose={() => setPasswordOpen(false)} />
    </Screen>
  );
}
